import { selectedScreen } from './nativeScreens.js'
import * as validators from './nativeClipboardValidators.generated'
import { COMPONENT_REGISTRY, componentInstanceName, reconcileManifestCapabilities } from './nativeSportsbookManifest'
import { reidentifyColumns } from './playerPropBoardModel'
import { builderIdentities, reidentifyBuilder } from './parlayBuilderModel'
import { cardDuplicationIssue, sectionDuplicationIssue } from './nativeDuplication'
import { isLayoutClipboard, layoutPasteIssue, pasteNativeLayout, validateLayoutClipboard } from './nativeLayoutClipboard'
import { gamePageLabel } from './gamePageModel'

const FIRST_CHARACTER_PATTERN = /^./
const CARD_POINTER_PATTERN = /^\/cards\/(\d+)/

export const NATIVE_CLIPBOARD_LIMIT = 131072
const FORMAT = 'wager-native-authoring'
const envelopeKeys = new Set(['format', 'version', 'organization', 'kind', 'content'])
const names = (value) => String(value).replaceAll('_', ' ').replace(FIRST_CHARACTER_PATTERN, (letter) => letter.toUpperCase())

function validateContent(kind, content, registry) {
  if (!content || typeof content !== 'object' || Array.isArray(content)) throw new Error('Copied content is invalid. Copy it again from App Builder.')
  const definition = registry.components.find((item) => item.type === content?.type)
  if (kind === 'section' && (!definition?.policy.removable || definition.policy.required)) throw new Error('This section cannot be copied or pasted.')
  const cardDefinition = registry.components.find((item) => item.type === 'featured_showcase')?.editor?.cards
  if (kind === 'card' && !cardDefinition?.kinds?.some((item) => item.kind === content?.kind)) throw new Error('This card type is not supported. Copy a registered Featured card.')
  const type = content?.type === 'sports_search' ? 'search' : content?.type
  const validate = validators[kind === 'event_layout' ? 'event_template' : kind === 'home_variation' ? 'home_scene' : `${kind === 'section' ? type : content.kind}_${kind === 'section' ? 'component' : 'card'}`]
  if (!validate) throw new Error('This content type is not supported by this editor.')
  if (!validate(content)) {
    // A section's cards validate as oneOf card kinds, so the first error names a
    // different kind's field; prefer the error about a field this card kind has.
    const cardAt = (path) => { const match = CARD_POINTER_PATTERN.exec(path); return match ? content.cards?.[Number(match[1])] : null }
    const fieldsFor = (card) => (card ? cardDefinition?.kinds?.find((item) => item.kind === card.kind)?.fields : definition?.editor?.fields) || []
    const keyOf = (item) => item.params.missingProperty || item.params.additionalProperty || item.instancePath.split('/').at(-1)
    const owns = (fields, key) => fields.some((item) => item.path === key || item.path.startsWith(`${key}.`))
    const error = validate.errors.find((item) => { const card = cardAt(item.instancePath); return card && owns(fieldsFor(card), keyOf(item)) }) || validate.errors[0]
    const card = cardAt(error.instancePath)
    const key = keyOf(error)
    const field = fieldsFor(card).find((item) => item.path === key || item.path.startsWith(`${key}.`))
    const subject = card ? `${card.headline || names(card.kind)} card: ` : ''
    throw new Error(`Copied ${names(kind).toLowerCase()} is invalid: ${subject}${field?.label || names(key || 'content')} ${error.keyword === 'additionalProperties' ? 'is not supported' : error.message}. Correct it in the source, then copy again.`)
  }
  if (isLayoutClipboard(kind)) { validateLayoutClipboard(kind, content); return }
  // Schema identity formats are checked above; duplicate identities inside one
  // payload are still invalid even though insertion would assign fresh values.
  const ids = kind === 'section' ? [content.id, content.slot_id, ...(content.cards ?? []).map((card) => card.id), ...(content.columns ?? []).map((column) => column.id), ...builderIdentities(content).map((item) => item.id), ...(content.collection ? [content.collection.id] : [])] : [content.id]
  if (new Set(ids).size !== ids.length) throw new Error('Copied content has repeated instance identities. Correct the source before copying.')
}

export function copyNativeContent(document, selection, organization, registry = COMPONENT_REGISTRY) {
  if (!organization) throw new Error('Choose an organization before copying content.')
  const component = selectedScreen(document, selection).components.find((item) => item.id === selection.componentId)
  const kind = selection.surface === 'events' ? 'event_layout' : selection.surface === 'scenes' ? 'home_variation' : selection.cardId ? 'card' : 'section'
  const content = kind === 'event_layout' ? document.experience?.event_templates.find((item) => selection.templateId ? item.id === selection.templateId : item.scope.kind === 'default')
    : kind === 'home_variation' ? document.experience?.home_scenes.find((item) => item.id === selection.sceneId)
      : selection.cardId ? component?.cards?.find((item) => item.id === selection.cardId) : component
  if (!content) throw new Error('Select a section, card, game page or Home variation to copy.')
  validateContent(kind, content, registry)
  const text = JSON.stringify({ format: FORMAT, version: 1, organization, kind, content })
  if (text.length > NATIVE_CLIPBOARD_LIMIT) throw new Error('This content is too large to copy. Copy individual cards instead.')
  return { text, kind, name: kind === 'event_layout' ? gamePageLabel(content) : isLayoutClipboard(kind) ? content.name : kind === 'section' ? componentInstanceName(content) : content.headline || names(content.kind) }
}

export function readNativeClipboard(text, organization, registry = COMPONENT_REGISTRY) {
  if (typeof text !== 'string' || text.length > NATIVE_CLIPBOARD_LIMIT) throw new Error('Pasted content is too large. Copy one section, card or page at a time.')
  let payload
  try { payload = JSON.parse(text) } catch { throw new Error('Paste a section, card or page copied from App Builder.') }
  if (!payload || payload.format !== FORMAT || payload.version !== 1 || !['section', 'card', 'event_layout', 'home_variation'].includes(payload.kind)
    || Object.keys(payload).some((key) => !envelopeKeys.has(key))) throw new Error('This clipboard format is not supported. Copy the content again in App Builder.')
  if (!organization || payload.organization !== organization) throw new Error('This content was copied in a different organization. Return to that organization to paste it.')
  validateContent(payload.kind, payload.content, registry)
  return payload
}

export function nativePasteIssue(document, selection, payload, registry = COMPONENT_REGISTRY, destination = {}) {
  if (isLayoutClipboard(payload.kind)) return layoutPasteIssue(document, selection, payload, destination)
  if (selection.surface !== 'home') return 'Open Home and select where to paste the section or card.'
  if (payload.kind === 'section') return sectionDuplicationIssue(document, payload.content, registry, selection.screenId)
  const component = selectedScreen(document, selection).components.find((item) => item.id === selection.componentId)
  const issue = cardDuplicationIssue(component, registry)
  return issue ? 'Select a Featured section with room for another card. ' + issue : null
}

export function pasteNativeContent(document, selection, text, organization, registry = COMPONENT_REGISTRY, uuid = () => crypto.randomUUID(), destination = {}) {
  const payload = readNativeClipboard(text, organization, registry)
  const issue = nativePasteIssue(document, selection, payload, registry, destination)
  if (issue) throw new Error(issue)
  if (isLayoutClipboard(payload.kind)) {
    const result = pasteNativeLayout(document, selection, payload, destination, uuid)
    return { ...result, document: reconcileManifestCapabilities(result.document, registry) }
  }
  const next = structuredClone(document)
  const screen = selectedScreen(next, selection)
  const components = screen.components
  const selectedIndex = components.findIndex((item) => item.id === selection.componentId)
  const content = structuredClone(payload.content)
  if (payload.kind === 'section') {
    content.id = uuid(); content.slot_id = uuid()
    for (const card of content.cards ?? []) card.id = uuid()
    if (content.collection) content.collection.id = uuid()
    reidentifyColumns(content, uuid)
    reidentifyBuilder(content, uuid)
    if (content.type === 'competition_markets' && screen?.context?.kind === 'competition') {
      content.competition = {
        sport: screen.context.sport,
        name: screen.context.competition,
      }
    }
    const requiredIndex = components.findIndex((item) => registry.components.find((definition) => definition.type === item.type)?.policy.required)
    const limit = requiredIndex === -1 ? components.length : requiredIndex
    const insertionIndex = selectedIndex < 0 ? limit : Math.min(selectedIndex + 1, limit)
    const anchor = components[insertionIndex - 1]?.id
    components.splice(insertionIndex, 0, content)
    for (const scene of screen.context ? [] : next.experience?.home_scenes ?? []) {
      const position = scene.component_order.indexOf(anchor)
      if (position !== -1) scene.component_order.splice(position + 1, 0, content.id)
    }
    return { document: reconcileManifestCapabilities(next, registry), selection: { componentId: content.id, cardId: null }, name: componentInstanceName(content), kind: 'section' }
  }
  const component = components[selectedIndex]
  const cardIndex = component.cards.findIndex((item) => item.id === selection.cardId)
  content.id = uuid()
  component.cards.splice(cardIndex < 0 ? component.cards.length : cardIndex + 1, 0, content)
  return { document: next, selection: { componentId: component.id, cardId: content.id }, name: content.headline || names(content.kind), kind: 'card' }
}
